import type { DocumentPreviewHighlight } from "components/Document/shared/DocumentPreview";

import {
  boxRegion,
  fieldPathOf,
  withBottomBuffer,
  type ManifestFormVariant,
} from "components/Document/ReviewInteraction";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import { FocusEvent, useCallback, useMemo, useState } from "react";

import { manifestFieldBox } from "../components/manifestFieldBoxes";

/**
 * The document pane follows the field the reviewer is in: it scrolls that
 * field's box on EPA Form 8700-22 into view and outlines it.
 *
 * Transcribing a poor scan means reading a value off the page and typing it into
 * a form, and the two are not laid out the same way — the reviewer loses their
 * place on the document every time they look down. Pointing at the box removes
 * the search half of that loop.
 *
 * The outline is a hint and nothing more. The region comes from the form's fixed
 * layout, not from anything measured on this document, so a rotated or cropped
 * scan lands it imprecisely.
 */

/** Which page of the PDF each part of the manifest was read from. */
export interface ReadingPages {
  /** The page the main 8700-22 is on, 1-based. Null when the model could not tell. */
  manifestPageNumber: number | null;
  /** Page per waste line, keyed by the line's printed number. */
  wasteLinePages: ReadonlyMap<string, number>;
  /** Page per transporter, keyed by the transporter's printed number. */
  transporterPages: ReadonlyMap<string, number>;
}

export const NO_READING_PAGES: ReadingPages = {
  manifestPageNumber: null,
  wasteLinePages: new Map(),
  transporterPages: new Map(),
};

/** Matches a repeated field path, e.g. `wasteLines.2.quantity`. */
const ROW_FIELD = /^(wasteLines|transporters)\.(\d+)\./;

/**
 * The page a reading captured for this field's own row, if it captured one.
 *
 * Kept apart from the manifest's page rather than falling back to it. A row's
 * own page is the only thing that knows where a continuation sheet physically
 * landed, and a row without one is not on the manifest's page by default — a
 * waste line past the fourth is on the 8700-22A, so answering "the page the
 * main form is on" paints the continuation sheet's geometry over the main
 * form's boxes. Which sheet the field belongs to is the better answer there,
 * and `pageToShow` has it.
 */
function rowPageFor(fieldPath: string, rowNumbers: RowNumbers, pages: ReadingPages): number | null {
  const row = ROW_FIELD.exec(fieldPath);
  if (row == null) return null;

  const [, group, indexText] = row;
  const index = safeParseInt(indexText);
  if (index == null) return null;
  const number =
    group === "wasteLines" ? rowNumbers.wasteLines[index] : rowNumbers.transporters[index];
  if (number == null) return null;

  const captured =
    group === "wasteLines" ? pages.wasteLinePages.get(number) : pages.transporterPages.get(number);
  return captured ?? null;
}

/**
 * Which page to point at, given what the reading said and how long the file is.
 *
 * The reading's own page is the only thing that knows where a continuation sheet
 * physically landed, so it wins whenever there is one. Everything else here is
 * for the manifests read before readings were kept apart from the working copy,
 * which have no reading at all — on some environments most of them — and which
 * used to get no outline whatsoever.
 *
 * The file's page count is what makes a fallback safe rather than a guess. The
 * two sheets are printed in a known order, so without a reading the 8700-22 is
 * the first page and an 8700-22A follows it; clamping to the count keeps both
 * inside a file that may be shorter than that.
 *
 * It is still a guess, and the one case it gets wrong is a bundle whose first
 * page is something else — a scale ticket, a weigh slip. That costs an outline
 * over the wrong page, on a pane that still pans and zooms freely, against
 * getting no outline at all on most of the backlog.
 */
function pageToShow({
  rowPage,
  manifestPage,
  variant,
  pageCount,
}: {
  /** The page the reading captured for this field's own row, if any. */
  rowPage: number | null;
  /** The page the reading put the main 8700-22 on, if it said. */
  manifestPage: number | null;
  variant: ManifestFormVariant;
  pageCount: number | null;
}): number | null {
  // Clamped where the file's length is known: a reading taken from an earlier
  // version of the file can name a page the one on screen does not have, and
  // jumping to it leaves the reviewer somewhere the viewer cannot show.
  const onFile = (page: number) =>
    pageCount == null ? page : Math.min(Math.max(page, 1), pageCount);

  // A row that carries its own page is the only thing that knows where a
  // continuation sheet actually landed, so nothing below improves on it.
  if (rowPage != null) return onFile(rowPage);

  // Where the main form is: what the reading said, or the first page once the
  // file's length is there to bound the guess. Without either there is nothing
  // to place this against.
  const mainForm = manifestPage ?? (pageCount == null ? null : 1);
  if (mainForm == null) return null;

  // The 8700-22A is printed after the 8700-22. That is a weaker claim than a
  // captured page, but a far better one than putting continuation-sheet
  // geometry on the main form's own page.
  return onFile(variant === "8700-22" ? mainForm : mainForm + 1);
}

/**
 * The printed numbers of the rows currently on the form, in form order.
 *
 * The form sorts rows by their printed number while a reading lists them in the
 * order the model returned them, so a row's position in one is not its position
 * in the other. Pages are captured per printed number for that reason, and this
 * is what turns a focused path's index back into the number it belongs to.
 */
export interface RowNumbers {
  wasteLines: readonly string[];
  transporters: readonly string[];
}

export function useDocumentFollowing({
  pages,
  rowNumbers,
  enabled = true,
  pageCount = null,
}: {
  pages: ReadingPages;
  rowNumbers: RowNumbers;
  /** False leaves the pane alone — nothing to follow on a non-manifest document. */
  enabled?: boolean;
  /**
   * How many pages the file turned out to have, or null before it has loaded.
   *
   * Only the viewer knows — nothing on the server records it — and it is what
   * lets a document with no reading be pointed at anyway. See `pageToShow`.
   */
  pageCount?: number | null;
}): {
  highlight: DocumentPreviewHighlight | null;
  onFocusCapture: (event: FocusEvent<HTMLElement>) => void;
} {
  const [focusedPath, setFocusedPath] = useState<string | null>(null);

  const onFocusCapture = useCallback((event: FocusEvent<HTMLElement>) => {
    const path = fieldPathOf(event.target);
    // Deliberately not cleared on blur. Focus leaving a field for a button, or
    // for the document pane itself, would otherwise pull the outline away at the
    // moment the reviewer looked up to read it.
    if (path != null) setFocusedPath(path);
  }, []);

  const highlight = useMemo(() => {
    if (!enabled || focusedPath == null) return null;

    const field = manifestFieldBox(focusedPath);
    if (field == null) return null;

    const region = boxRegion(field.variant, field.box, field.rowIndex);
    if (region == null) return null;

    const page = pageToShow({
      rowPage: rowPageFor(focusedPath, rowNumbers, pages),
      manifestPage: pages.manifestPageNumber,
      variant: field.variant,
      pageCount,
    });
    // Nothing said where this is and the file has not loaded to say how long it
    // is, so there is nowhere to point yet.
    if (page == null) return null;

    // No label. The outline points at a box on a form the reviewer is reading,
    // and a caption sitting over the line above it covers data they cannot
    // afford to lose — on a dense form there is nowhere it does not. It also
    // names the field they just put the caret in, which is the one thing they
    // already know. The region sheet still labels its outlines, because there
    // every box is drawn at once and the number is how you tell them apart.
    //
    // Grown downward, because scan error has a direction: a skewed or short-
    // trimmed scan puts the printed form lower on the page, never higher.
    return { page, region: withBottomBuffer(region) };
  }, [enabled, focusedPath, pages, rowNumbers, pageCount]);

  return { highlight, onFocusCapture };
}
